import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  FileText,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const QUICK_ACTIONS = [
  { label: "View Payslips", path: "/payslip", desc: "Open the payslip viewer" },
  { label: "Bulk Payslips", path: "/payslip?tab=bulk", desc: "Generate payslips in bulk" },
  { label: "Finalize Payroll", path: "/payroll-finalize", desc: "Verify, approve and lock a payroll run" },
  { label: "Payroll Runs", path: "/payroll-runs", desc: "View payroll run history and status" },
  { label: "Employee Salary Structure", path: "/employeesalarystructure", desc: "Check salary package assignments" },
  { label: "Process Payroll", path: "/payroll-process", desc: "Run or manage payroll processing" },
];

const PAYSLIP_TROUBLESHOOTING = [
  {
    problem: "Payslip not available — shows \"Pending finalization\"",
    check: "The payroll run for the selected period has been processed but has not completed the finalization lifecycle. Payslips are only available after a payroll run is locked.",
    action: "Go to Finalize Payroll and complete all three steps: Verify → Approve → Lock. Payslips become available immediately after locking.",
    path: "/payroll-finalize",
    pathLabel: "Finalize Payroll",
  },
  {
    problem: "No payroll runs appear when selecting an employee",
    check: "No payroll has been processed for the selected employee in any pay period, or the employee was not included in the payroll run.",
    action: "Go to Payroll Runs to verify that a payroll run exists for the relevant period and that it includes the employee. If no run exists, create one from Process Payroll.",
    path: "/payroll-runs",
    pathLabel: "Payroll Runs",
  },
  {
    problem: "Employee not appearing in the payslip employee list",
    check: "The employee may not have an active status, may be missing a salary structure assignment, or was not part of the payroll run for the selected period.",
    action: "Go to Employee Salary Structure and verify the employee has a salary template and Annual CTC assigned. Also confirm the employee's status is Active.",
    path: "/employeesalarystructure",
    pathLabel: "Employee Salary Structure",
  },
  {
    problem: "Payslip amounts appear incorrect",
    check: "Payslip amounts are calculated from the employee's salary structure, attendance data (payable days, LOP days, salary factor), and statutory deductions (PF, ESI, PT, TDS). If any of these inputs were incorrect at the time of processing, the payslip will reflect those values.",
    action: "Review the employee's salary structure, attendance records for the pay period, and statutory settings. If corrections are needed, the payroll run must be reset and reprocessed from Process Payroll.",
    path: "/employeesalarystructure",
    pathLabel: "Employee Salary Structure",
  },
  {
    problem: "Cannot download payslip as PDF",
    check: "Payslip download is only available for payroll runs that have been locked. If the payslip viewer shows a \"Pending finalization\" or \"In progress\" status badge, the payroll run is not yet finalized.",
    action: "Go to Finalize Payroll and complete the Verify → Approve → Lock lifecycle for the relevant payroll run. After locking, return to the payslip viewer to download.",
    path: "/payroll-finalize",
    pathLabel: "Finalize Payroll",
  },
  {
    problem: "Bulk payslip generation not working",
    check: "Bulk payslip generation requires at least one locked payroll run. If no locked runs exist for the selected period, the bulk generation will not produce results.",
    action: "Verify that the payroll run for the target period is locked. If not, go to Finalize Payroll to complete the lifecycle. Then use the Bulk Payslips tab to generate.",
    path: "/payslip?tab=bulk",
    pathLabel: "Bulk Payslips",
  },
  {
    problem: "Employee can only see their own payslips",
    check: "This is expected behavior. Employees in self-service mode can only view their own payslips. The employee field is auto-filled and cannot be changed.",
    action: "If admin or HR access is needed to view other employees' payslips, verify the user's role in Settings. Admin and HR roles can view all employees' payslips.",
    path: "/payslip",
    pathLabel: "Payslip Viewer",
  },
];

export default function PayslipSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return PAYSLIP_TROUBLESHOOTING;
    return PAYSLIP_TROUBLESHOOTING.filter(
      (t) =>
        t.problem.toLowerCase().includes(q) ||
        t.check.toLowerCase().includes(q) ||
        t.action.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-violet">
              <FileText size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Payslip Issues</h1>
              <p className="hs-hero-sub">
                Troubleshoot problems viewing, generating, or downloading payslips.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search payslip issues — e.g. pending, download, bulk..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant page to check or fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Payslip Troubleshooting (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Payslip Troubleshooting</h2>
            <p className="hs-section-sub">
              Select your issue to see what to check and how to resolve it
            </p>
          </div>

          {q && filteredItems.length === 0 && (
            <div className="sc-no-results">
              No payslip issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredItems.map((item, idx) => {
              const isOpen = openItem === idx;
              return (
                <div key={item.problem} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenItem(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.problem}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Check
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.check}</p>
                          </div>
                          <div style={{ marginBottom: item.path ? 12 : 0 }}>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Next Action
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.action}</p>
                          </div>
                          {item.path && (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                color: "#1a437d",
                                background: "rgba(26,67,125,0.06)",
                                border: "1px solid rgba(26,67,125,0.15)",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(item.path)}
                            >
                              {item.pathLabel}
                              <ExternalLink size={13} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed payslip information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed payslip information?</h2>
              <p className="sc-info-row-desc">
                Visit the Payroll Help Guide for detailed documentation, FAQs and step-by-step payroll instructions.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/payroll")}
            >
              Open Payroll Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
